import { useFormikContext } from 'formik';
import { GitBranch, RefreshCw } from 'lucide-react';
import { useEffect } from 'react';

import { BetaAlert } from '@/react/portainer/environments/update-schedules/common/BetaAlert';
import { useSettings } from '@/react/portainer/settings/queries';

import { FormControl } from '@@/form-components/FormControl';
import { Input } from '@@/form-components/Input';
import { BoxSelector } from '@@/BoxSelector';
import { BoxSelectorOption } from '@@/BoxSelector/types';
import { TextTip } from '@@/Tip/TextTip';

import { FormValues, WorkflowEngine } from './types';
import { WizardMode, useWizardContext } from './WizardContext';

const engineOptions: ReadonlyArray<BoxSelectorOption<WorkflowEngine>> = [
  {
    id: 'engine-edge-stack',
    value: 'edgeStack',
    icon: GitBranch,
    iconType: 'badge',
    label: 'Edge stack',
    description:
      'Deploys each artifact as an Edge stack and redeploys it when the source moves.',
  },
  {
    id: 'engine-reconcile',
    value: 'reconcile',
    icon: RefreshCw,
    iconType: 'badge',
    label: 'Reconciliation (Beta)',
    description:
      'Keeps Kubernetes environments matching the source continuously, and reports what drifted.',
  },
] as const;

/**
 * Chooses how a workflow's artifacts are kept in step with their source.
 *
 * The choice belongs to the whole workflow rather than to one artifact, because
 * the reconcile interval below is a workflow-level schedule: an artifact using a
 * different engine would simply ignore it.
 */
export function EngineFieldset() {
  const { values, initialValues, setFieldValue, errors } =
    useFormikContext<FormValues>();

  // Migrating runs one way: the reconciliation engine adopts what the Edge
  // stacks deployed, where the reverse would have to hand the resources back to
  // stacks that no longer exist.
  // Compared against what the workflow was loaded as, so a workflow already on
  // the reconciliation engine is not described as migrating to it.
  const isEditing = useWizardContext().mode === WizardMode.Edit;
  const isMigrating =
    isEditing &&
    initialValues.engine === 'edgeStack' &&
    values.engine === 'reconcile';

  // A workflow already on the reconciliation engine cannot leave it either:
  // the update would be read as a migration, which would find nothing to
  // migrate and drop every other change behind a success message.
  const edgeStackLocked =
    isMigrating || (isEditing && initialValues.engine === 'reconcile');

  // Edge stacks are an Edge compute feature, so with it switched off the only
  // engine left is the reconciliation one. Read explicitly against false so a
  // settings request still in flight does not take the choice away and put it
  // back.
  const settingsQuery = useSettings();
  const edgeComputeDisabled =
    settingsQuery.data?.EnableEdgeComputeFeatures === false;

  // A new workflow starts on the Edge stack engine, which is not an option
  // here. Only a new one: moving an existing workflow would read as a
  // migration, which removes its Edge stacks.
  const forceReconcile = edgeComputeDisabled && !isEditing;
  useEffect(() => {
    if (forceReconcile && values.engine === 'edgeStack') {
      setFieldValue('engine', 'reconcile');
    }
  }, [forceReconcile, values.engine, setFieldValue]);

  return (
    <>
      <BoxSelector
        slim
        radioName="workflow-engine"
        value={values.engine}
        options={engineOptions.map((option) => ({
          ...option,
          disabled:
            (edgeStackLocked || edgeComputeDisabled) &&
            option.value === 'edgeStack',
        }))}
        onChange={handleEngineChange}
      />

      {edgeComputeDisabled && (
        <TextTip color="blue">
          Edge compute features are switched off, so a workflow can only use the
          reconciliation engine.
        </TextTip>
      )}

      {isMigrating && (
        <TextTip color="orange">
          Saving migrates this workflow to the reconciliation engine and removes
          its Edge stacks. What they deployed keeps running, and this cannot be
          undone.
        </TextTip>
      )}

      {values.engine === 'reconcile' && (
        <>
          {/* Stacked rather than left to flow: a TextTip is inline, so the beta
              label and the note below it ran together on one line. The margin
              is the tips' own: a FormControl brings no space above it. */}
          <div className="mb-4 flex flex-col items-start gap-2">
            <BetaAlert message="Beta feature - the reconciliation engine is still under active development, and how it behaves may change." />

            <TextTip color="blue">
              Reconciled workflows target Edge groups, environment groups or
              individual environments, and support Edge Kubernetes environments
              only.
            </TextTip>
          </div>

          <FormControl
            label="Reconcile interval"
            tooltip="How often each environment checks itself against the source, even when the source has not moved. This is what makes drift detectable. At least 1m."
            inputId="reconcile-interval"
            errors={errors.reconcile?.interval}
          >
            <Input
              id="reconcile-interval"
              data-cy="workflow-reconcile-interval-input"
              placeholder="5m"
              value={values.reconcile.interval}
              onChange={(e) =>
                setFieldValue('reconcile.interval', e.target.value)
              }
            />
          </FormControl>
        </>
      )}
    </>
  );

  /**
   * Switching engines drops the targets the new one cannot accept.
   *
   * Both engines take Edge groups, so those are kept. Environment groups and
   * individual environments belong to the reconciliation engine alone, and an
   * Edge stack rejects them.
   */
  function handleEngineChange(engine: WorkflowEngine) {
    setFieldValue('engine', engine);

    if (engine === 'edgeStack') {
      values.artifacts.forEach((_, index) => {
        setFieldValue(`artifacts.${index}.environmentGroups`, []);
        setFieldValue(`artifacts.${index}.environments`, []);
      });
    }
  }
}
